import React from 'react'
import styled from '@emotion/styled'
import { alpha } from '@mui/material/styles'
import { Box, Paper, Typography, Theme } from '@mui/material'

// Icons
import GroupIcon from '@mui/icons-material/Group'
import TimelineIcon from '@mui/icons-material/Timeline'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'

const MetricsContainer = styled(Box)<{ theme?: Theme }>(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: theme?.spacing(3),
  marginBottom: theme?.spacing(4),
}))

const MetricCard = styled(Paper)<{ theme?: Theme }>(({ theme }) => {
  if (!theme) {
    return {}
  }
  const isDark = theme.palette.mode === 'dark'
  return {
    padding: theme.spacing(3),
    borderRadius: '12px',
    background: isDark
      ? alpha(theme.palette.background.paper, 0.72)
      : theme.palette.background.paper,
    border: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.12) : theme.palette.divider}`,
    boxShadow: theme.shadows[isDark ? 4 : 1],
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  }
})

const MetricHeader = styled(Box)<{ theme?: Theme }>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme?.spacing(1.5),
}))

const MetricIcon = styled(Box)<{ theme?: Theme }>(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme
    ? alpha(theme.palette.primary.main, 0.15)
    : 'rgba(56, 154, 203, 0.15)',
  color: theme?.palette?.primary?.main,
}))

const MetricValue = styled(Typography)<{ theme?: Theme }>(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: theme?.palette.text.primary ?? '#fff',
  lineHeight: 1,
}))

const MetricTrend = styled(Box)<{ trend: 'up' | 'down' | 'neutral'; theme?: Theme }>(
  ({ theme, trend }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme?.spacing(0.5),
    color:
      trend === 'up'
        ? (theme?.palette.success?.main ?? '#4caf50')
        : trend === 'down'
          ? (theme?.palette.error?.main ?? '#f44336')
          : (theme?.palette.primary.main ?? '#389acb'),
    fontSize: '0.875rem',
    fontWeight: 500,
  })
)

interface MetricData {
  value: number
  trend: 'up' | 'down' | 'neutral'
  change: string
}

interface ServerMetrics {
  activeUsers: MetricData
  totalRooms: MetricData
}

interface AdminMetricsProps {
  metrics: ServerMetrics
}

const AdminMetrics: React.FC<AdminMetricsProps> = ({ metrics }) => {
  return (
    <MetricsContainer>
      <MetricCard>
        <MetricHeader>
          <MetricIcon>
            <GroupIcon />
          </MetricIcon>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Active Users
            </Typography>
            <MetricValue>{metrics.activeUsers.value}</MetricValue>
          </Box>
        </MetricHeader>
        <MetricTrend trend={metrics.activeUsers.trend}>
          <TimelineIcon fontSize="small" />
          {metrics.activeUsers.change} from last hour
        </MetricTrend>
      </MetricCard>

      <MetricCard>
        <MetricHeader>
          <MetricIcon>
            <MeetingRoomIcon />
          </MetricIcon>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Rooms
            </Typography>
            <MetricValue>{metrics.totalRooms.value}</MetricValue>
          </Box>
        </MetricHeader>
        <MetricTrend trend={metrics.totalRooms.trend}>
          <TimelineIcon fontSize="small" />
          {metrics.totalRooms.change} from last hour
        </MetricTrend>
      </MetricCard>
    </MetricsContainer>
  )
}

export default AdminMetrics
